"use client";

import { DATA_LAYER_EVENT_NAMES } from "@/constants/dataLayer";
import useRecaptcha from "@/hooks/useRecaptcha";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  initialState,
  pointConvertReducer,
} from "@/reducers/pointConvertReducer";
import { paymentService } from "@/services/paymentService";
import { pointConvertService } from "@/services/pointConvertService";
import { sendGTMEvent } from "@next/third-parties/google";
import { createContext, useContext, useReducer } from "react";
import { useAuth } from "./AuthContext";
import { useCurrency } from "./CurrencyContext";
import { useModalHelpers } from "./ModalContext";

const PointConvertContext = createContext();

export function PointConvertProvider({ children }) {
  const [state, dispatch] = useReducer(pointConvertReducer, initialState);
  const { currency } = useCurrency();
  const { session } = useAuth();
  const { getCaptchaToken } = useRecaptcha();
  const { errorModal, infoModal, successModal } = useModalHelpers();
  const t = useTranslationDetector();

  const setLoading = (loading) => {
    dispatch({ type: "SET_LOADING", payload: loading });
  };
  const setActiveStep = (activeStep) => {
    dispatch({ type: "SET_ACTIVE_STEP", payload: activeStep });
  };
  const setActiveTab = (activeTab) => {
    dispatch({ type: "SET_ACTIVE_TAB", payload: activeTab });
  };
  const setCardProgram = (cardProgram) => {
    dispatch({ type: "SET_CARD_PROGRAM", payload: cardProgram });
  };
  const setSelectedSavedCard = (selectedSavedCard) => {
    dispatch({ type: "SET_SELECTED_SAVED_CARD", payload: selectedSavedCard });
  };
  const setCardFormData = (cardFormData) => {
    dispatch({ type: "SET_CARD_FORM_DATA", payload: cardFormData });
  };
  const setPointInfo = (pointInfo) => {
    dispatch({ type: "SET_POINT_INFO", payload: pointInfo });
  };

  const set3DModalOpen = (is3DModalOpen) => {
    dispatch({ type: "SET_3D_MODAL_OPEN", payload: is3DModalOpen });
  };
  const setIframeSrc = (iframeSrc) => {
    dispatch({ type: "SET_IFRAME_SRC", payload: iframeSrc });
  };
  const setConvertedPoints = (convertedPoints) => {
    dispatch({ type: "SET_CONVERTED_POINTS", payload: convertedPoints });
  };

  const setPaymentId = (paymentId) => {
    dispatch({ type: "SET_PAYMENT_ID", payload: paymentId });
  };

  const setIsDebitCard = (isDebitCard) => {
    dispatch({ type: "SET_IS_DEBIT_CARD", payload: isDebitCard });
  };

  const pointInquiryNewCard = async (data) => {
    try {
      const RecaptchaKey = await getCaptchaToken("ccpointinquiry");
      const response = await pointConvertService.pointInquiryNewCard({
        ...data,
        MembershipType: session?.UserDetail?.MembershipType,
        RecaptchaKey,
      });

      if (!response?.Message) {
        if (response?.CardExists) {
          infoModal({
            message: t("common.cardExistsInfo"),
            isBottomSheet: true,
          });
        } else {
          setCardFormData(data);
          setPointInfo(response);
          setActiveStep(1);
          sendGTMEvent({
            event: "custom_event",
            event_category: "convert_credit_card_points",
            event_action: DATA_LAYER_EVENT_NAMES.QUERY_CREDIT_CARD_POINTS,
          });
        }
      } else {
        setCardFormData(null);
        errorModal({
          message: t(`${response.Type}.${response.Name}`),
          isBottomSheet: true,
        });
      }
    } catch (error) {
      errorModal({
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
        isBottomSheet: true,
      });
    } finally {
    }
  };

  const pointInquirySavedCard = async () => {
    try {
      const RecaptchaKey = await getCaptchaToken("storedcardpointinquiry");
      const response = await pointConvertService.pointInquirySavedCard({
        CardId: state?.selectedSavedCard?.Id,
        RecaptchaKey,
      });

      if (!response.Message) {
        setPointInfo(response);
        setActiveStep(1);
        sendGTMEvent({
          event: "custom_event",
          event_category: "convert_credit_card_points",
          event_action: DATA_LAYER_EVENT_NAMES.QUERY_CREDIT_CARD_POINTS,
        });
      } else {
        setCardFormData(null);
        errorModal({
          title: t("common.failed"),
          message: t(`${response.Type}.${response.Name}`),
          size: "sm",
          showCancel: false,
          isBottomSheet: true,
        });
      }
    } catch (error) {
      errorModal({
        title: t("common.failed"),
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
        size: "sm",
        showCancel: false,
        isBottomSheet: true,
      });
    } finally {
    }
  };

  const pointConvertWithNewCard = async (pointAmount) => {
    try {
      // const RecaptchaKey = await getCaptchaToken("ccpointconvert");
      const response = await pointConvertService.pointConvertWithNewCard({
        ...state?.cardFormData,
        Amount: pointAmount,
        UserId: session?.UserId,
        ReturnUrl: `${window.location.origin}/bank-three-d-return?hideLayout=true&pointConvert=true&isSavedCard=false`,
        // RecaptchaKey,
      });
      if (!response.ContinueUrl) {
        errorModal({
          message: t(`${response.Type}.${response.Name}`),
          isBottomSheet: true,
        });
      } else {
        set3DModalOpen(true);
        setIframeSrc(response.ContinueUrl);
        sendGTMEvent({
          event: "custom_event",
          event_category: "convert_credit_card_points",
          event_action: DATA_LAYER_EVENT_NAMES.CONVERT_TO_TURNA_POINTS,
          credit_card_points_total: state?.pointInfo?.Point,
          credit_card_points_to_convert: pointAmount,
          credit_card_type: state?.cardProgram?.CardType,
          credit_card_bank: state?.pointInfo?.Program,
        });
      }
    } catch (error) {
      errorModal({
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
        isBottomSheet: true,
      });
    } finally {
    }
  };

  const pointConvertWithSavedCard = async (pointAmount) => {
    try {
      // const RecaptchaKey = await getCaptchaToken("ccpointconvert");
      const response = await pointConvertService.pointConvertWithSavedCard({
        Amount: pointAmount,
        CardId: state?.selectedSavedCard?.Id,
        ReturnUrl: `${window.location.origin}/bank-three-d-return?hideLayout=true&pointConvert=true&isSavedCard=true`,
        // RecaptchaKey,
      });
      if (!response.ContinueUrl) {
        errorModal({
          message: t(`${response.Type}.${response.Name}`),
          isBottomSheet: true,
        });
      } else {
        set3DModalOpen(true);
        setIframeSrc(response.ContinueUrl);
        sendGTMEvent({
          event: "custom_event",
          event_category: "convert_credit_card_points",
          event_action: DATA_LAYER_EVENT_NAMES.CONVERT_TO_TURNA_POINTS,
          credit_card_points_total: state?.pointInfo?.Point,
          credit_card_points_to_convert: pointAmount,
          credit_card_type: state?.cardProgram?.CardType,
          credit_card_bank: state?.pointInfo?.Program,
        });
      }
    } catch (error) {
      errorModal({
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
        isBottomSheet: true,
      });
    } finally {
    }
  };

  const completePointConvertPaymentNewCard = async (payload, callback) => {
    try {
      const response =
        await pointConvertService.completePointConvertPaymentNewCard(payload);
      if (!response.Message) {
        callback?.();
        setConvertedPoints(response);
        setActiveStep(2);
        sendGTMEvent({
          event: "custom_event",
          event_category: "convert_credit_card_points",
          event_action: DATA_LAYER_EVENT_NAMES.CONVERT_TO_TURNA_POINTS_COMPLETE,
          credit_card_points_total: response?.TotalUserPoint,
          credit_card_points_to_convert: response?.ConvertedPointAmount,
          converted_turna_points: response?.ConvertedPointAmount,
          credit_card_type: state?.cardProgram?.CardType,
          credit_card_bank: state?.pointInfo?.Program,
          saved_credit_card: "new",
        });
      } else {
        errorModal({
          message: t(`${response.Type}.${response.Name}`),
          isBottomSheet: true,
        });
      }
    } catch (error) {
      errorModal({
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
      });
    }
  };

  const completePointConvertPaymentSavedCard = async (payload, callback) => {
    try {
      const response =
        await pointConvertService.completePointConvertPaymentSavedCard(payload);
      if (!response.Message) {
        callback?.();
        setConvertedPoints(response);
        setActiveStep(2);
        sendGTMEvent({
          event: "custom_event",
          event_category: "convert_credit_card_points",
          event_action: DATA_LAYER_EVENT_NAMES.CONVERT_TO_TURNA_POINTS_COMPLETE,
          credit_card_points_total: response?.TotalUserPoint,
          credit_card_points_to_convert: response?.ConvertedPointAmount,
          converted_turna_points: response?.ConvertedPointAmount,
          credit_card_type: state?.cardProgram?.CardType,
          credit_card_bank: state?.pointInfo?.Program,
          saved_credit_card: "saved",
        });
      } else {
        errorModal({
          message: t(`${response.Type}.${response.Name}`),
          isBottomSheet: true,
        });
      }
    } catch (error) {
      errorModal({
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
      });
    }
  };

  const getBinDetails = async (binNumber) => {
    if (binNumber.length < 6) {
      setCardProgram(null);
      setIsDebitCard(false);
      return;
    }

    try {
      const response = await paymentService.getBinDetails({
        BinNumber: binNumber,
        Currency: currency.toUpperCase(),
      });

      if (response.Data) {
        setCardProgram(response.Data);
        setIsDebitCard(response.Data.IsDebit);
      }
    } catch (error) {
    } finally {
    }
  };

  const periodConversion = async () => {
    try {
      const response = await pointConvertService.periodConversion({
        UserId: session?.UserId,
        CardId: state?.selectedSavedCard?.Id,
      });
      if (!response.Message || !response.HasError) {
        successModal({
          message: t("common.periodConversionSuccess"),
          isBottomSheet: true,
        });
        setActiveStep(0);
        setActiveTab(1);
      } else {
        errorModal({
          message: t(`${response.Type}.${response.Name}`),
          isBottomSheet: true,
        });
      }
    } catch (error) {
      errorModal({
        message: t(`${error.response.data.Type}.${error.response.data.Name}`),
      });
    } finally {
    }
  };

  const getPaymentConfiguration = async () => {
    try {
      const response = await paymentService.getPaymentConfiguration();
      if (response?.Payment) {
        dispatch({
          type: "SET_CARD_HOLDER_IS_REQUIRED",
          payload: response?.Payment?.CardHolderIsRequired,
        });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const value = {
    state,
    setLoading,
    setActiveStep,
    setActiveTab,
    getBinDetails,
    pointInquiryNewCard,
    setSelectedSavedCard,
    pointInquirySavedCard,
    pointConvertWithNewCard,
    set3DModalOpen,
    completePointConvertPaymentNewCard,
    completePointConvertPaymentSavedCard,
    pointConvertWithSavedCard,
    periodConversion,
    setPaymentId,
    getPaymentConfiguration,
  };

  return (
    <PointConvertContext.Provider value={value}>
      {children}
    </PointConvertContext.Provider>
  );
}

export function usePointConvert() {
  const context = useContext(PointConvertContext);
  if (context === undefined) {
    throw new Error(
      "usePointConvert must be used within a PointConvertProvider",
    );
  }
  return context;
}
